import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { notificationService, Notification } from '@/services/notificationService';

interface UseNotificationsPageResult {
  notifications: Notification[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  unreadCount: number;
  unreadOnly: boolean;
  setUnreadOnly: (v: boolean) => void;
  loadMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

const PAGE_SIZE = 50;

export function useNotificationsPage(): UseNotificationsPageResult {
  const { activeOrganization, currentUser } = useAuth();
  const orgId = activeOrganization?.id;
  const uid = currentUser?.uid;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [unreadOnly, setUnreadOnlyState] = useState(false);

  const cursorRef = useRef<QueryDocumentSnapshot | null>(null);

  const fetchFirst = useCallback(
    async (unreadOnlyVal: boolean) => {
      if (!orgId) return;
      setLoading(true);
      cursorRef.current = null;
      try {
        const { notifications: notifs, lastDoc } = await notificationService.getNotificationsPage(
          orgId,
          { limit: PAGE_SIZE, unreadOnly: unreadOnlyVal, currentUserId: uid }
        );
        setNotifications(notifs);
        setHasMore(notifs.length === PAGE_SIZE);
        cursorRef.current = lastDoc;
        const count = await notificationService.getUnreadCount(orgId, { currentUserId: uid });
        setUnreadCount(count);
      } finally {
        setLoading(false);
      }
    },
    [orgId, uid]
  );

  useEffect(() => {
    fetchFirst(unreadOnly);
  }, [fetchFirst, unreadOnly]);

  const setUnreadOnly = useCallback(
    (v: boolean) => {
      setUnreadOnlyState(v);
    },
    []
  );

  const loadMore = useCallback(async () => {
    if (!orgId || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { notifications: more, lastDoc } = await notificationService.getNotificationsPage(
        orgId,
        { limit: PAGE_SIZE, cursor: cursorRef.current, unreadOnly, currentUserId: uid }
      );
      setNotifications((prev) => [...prev, ...more]);
      setHasMore(more.length === PAGE_SIZE);
      cursorRef.current = lastDoc;
    } finally {
      setLoadingMore(false);
    }
  }, [orgId, uid, loadingMore, hasMore, unreadOnly]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!orgId || !uid) return;
      await notificationService.markAsRead(orgId, id, uid);
      setNotifications((prev) =>
        unreadOnly
          ? prev.filter((n) => n.id !== id)
          : prev.map((n) =>
              n.id === id
                ? { ...n, read: true, readBy: [...n.readBy, uid], readAt: new Date() }
                : n
            )
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    },
    [orgId, uid, unreadOnly]
  );

  const markAllAsRead = useCallback(async () => {
    if (!orgId || !uid) return;
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    await notificationService.markAllAsRead(orgId, uid, unreadIds);
    setNotifications((prev) =>
      unreadOnly
        ? []
        : prev.map((n) =>
            n.read ? n : { ...n, read: true, readBy: [...n.readBy, uid], readAt: new Date() }
          )
    );
    setUnreadCount(0);
  }, [orgId, uid, notifications, unreadOnly]);

  const deleteNotification = useCallback(
    async (id: string) => {
      if (!orgId) return;
      const target = notifications.find((n) => n.id === id);
      await notificationService.deleteNotification(orgId, id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (target && !target.read) setUnreadCount((c) => Math.max(0, c - 1));
    },
    [orgId, notifications]
  );

  return {
    notifications,
    loading,
    loadingMore,
    hasMore,
    unreadCount,
    unreadOnly,
    setUnreadOnly,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}
